import { memo, useMemo } from 'react';
import { useRoutes } from 'react-router-dom';

import accountRoutes from '../account/account.routes';
import { UserAccountLayout } from '../account/layouts/account/account';
import { CashierLayout } from '../cashier/layout/cashier-layout';
import cashierRoutes from '../cashier/routes/cashier.routes';
import { CrabLandingPage } from '../crab-landing-page/components/crab-landing-page';
import { CrabPromo } from '../crab-promos/containers/crab-promo';
import crabPromoRoutes from '../crab-promos/crab-promo.routes';
import documentsRoutes from '../documents/documents.routes';
import { DocumentsLayout } from '../documents/layout/documents';
import { Footer } from '../footer/containers/footer';
import gameplayRoutes from '../gameplay/gameplay.routes';
import liveSpinsRoutes from '../live-spins/live-spins.routes';
import { Merchandise } from '../merchandise/containers/merchandise';
import merchandiseRoutes from '../merchandise/merchandise.routes';
import { RegistrationLayout } from '../registration/layout/registration';
import registrationRoutes from '../registration/registration.routes';
import { ContextNavigate } from '../shared/components/context-navigate/context-navigate';
import splashRoutes from '../splash/splash.routes';
import sportsRoutes from '../sports/sports.routes';
import verificationRoutes from '../verification/verification.routes';

import { LocaleRedirect } from './components/locale-redirect/locale-redirect';
import { AuthenticationGuard, AuthType } from './guards/authentication-guard';
import { LocationGuard } from './guards/location-guard';
import { Main } from './layouts/main/main';
import { Logout } from './routes/logout';
import { NotFound } from './routes/not-found';
import { GeoRestricted } from './routes/restricted';
import { Guard } from './util/guard';

export const AppRouter = memo(() => {
    const appRoutes = useRoutes([
        {
            index: true,
            element: <LocaleRedirect />,
        },
        {
            path: '/:locale',
            element: <LocaleRedirect />,
            children: [
                { index: true, element: <ContextNavigate to="/sports" /> },
                {
                    path: '404',
                    element: <NotFound />,
                },
                {
                    path: 'restricted',
                    element: <GeoRestricted />,
                },
                {
                    element: <Guard guards={[<LocationGuard key={'location'} />]} />,
                    children: [
                        // Gameplay has its own layout due to it needing control over
                        // the nav bars sizes and visibility
                        {
                            path: 'gameplay',
                            children: gameplayRoutes,
                        },
                        {
                            path: 'logout',
                            element: <Logout />,
                        },
                        {
                            path: 'app/footer',
                            element: <Footer />,
                        },
                        {
                            path: 'app/promo',
                            element: <CrabPromo />,
                        },
                        {
                            path: 'app/merch',
                            element: <Merchandise />,
                        },
                        {
                            path: 'partner/:name',
                            element: <CrabLandingPage />,
                        },
                        {
                            element: <Main />,
                            children: [
                                {
                                    path: 'registration',
                                    element: (
                                        <Guard
                                            guards={useMemo(
                                                () => [
                                                    <AuthenticationGuard
                                                        type={AuthType.REQUIRE_UNAUTHENTICATED}
                                                        key={'auth'}
                                                    />,
                                                ],
                                                [],
                                            )}
                                        >
                                            <RegistrationLayout />
                                        </Guard>
                                    ),
                                    children: registrationRoutes,
                                },
                                {
                                    path: 'documents',
                                    element: <DocumentsLayout />,
                                    children: documentsRoutes,
                                },
                                {
                                    path: 'sports',
                                    children: sportsRoutes,
                                },
                                {
                                    path: 'merchandise',
                                    children: merchandiseRoutes,
                                },
                                {
                                    path: 'pickem',
                                    children: splashRoutes,
                                },
                                // {
                                //     path: 'crab-club',
                                //     children: sportClubRoutes,
                                // },
                                {
                                    path: 'promo',
                                    children: crabPromoRoutes,
                                },
                                {
                                    path: 'live-spins',
                                    children: liveSpinsRoutes,
                                },
                                {
                                    path: 'account',
                                    element: (
                                        <Guard
                                            guards={useMemo(
                                                () => [<AuthenticationGuard key={'auth'} />],
                                                [],
                                            )}
                                        >
                                            <UserAccountLayout />
                                        </Guard>
                                    ),
                                    children: accountRoutes,
                                },
                                {
                                    path: 'cashier',
                                    element: (
                                        <Guard
                                            guards={useMemo(
                                                () => [<AuthenticationGuard key={'auth'} />],
                                                [],
                                            )}
                                        >
                                            <CashierLayout />
                                        </Guard>
                                    ),
                                    children: cashierRoutes,
                                },
                                ...verificationRoutes,
                            ],
                        },
                    ],
                },
                {
                    path: '*',
                    element: <LocaleRedirect />,
                    children: [
                        {
                            index: true,
                            path: '*',
                            element: <ContextNavigate to={'/404'} />,
                        },
                    ],
                },
            ],
        },
    ]);

    return appRoutes;
});
